import { useRef } from 'react';
import Toaster, { ToasterRef } from './toast';
import { createContext, useContext, ReactNode } from 'react';

interface ToastContextType {
  show: (message: string, variant?: 'success' | 'error' | 'info' | 'warning', title?: string) => void;
  success: (message: string, title?: string) => void;
  error: (message: string, title?: string) => void;
  info: (message: string, title?: string) => void;
  warning: (message: string, title?: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const toasterRef = useRef<ToasterRef>(null);

  const show = (message: string, variant: 'success' | 'error' | 'info' | 'warning' = 'success', title?: string) => {
    toasterRef.current?.show({
      title,
      message,
      variant: variant === 'info' ? 'info' : variant,
      position: 'top-center',
      duration: variant === 'error' ? 5000 : 3000,
    });
  };

  const success = (message: string, title?: string) => {
    show(message, 'success', title);
  };

  const error = (message: string, title?: string) => {
    show(message, 'error', title);
  };

  const info = (message: string, title?: string) => {
    show(message, 'info', title);
  };

  const warning = (message: string, title?: string) => {
    show(message, 'warning', title);
  };

  return (
    <ToastContext.Provider value={{ show, success, error, info, warning }}>
      {children}
      <Toaster ref={toasterRef} />
    </ToastContext.Provider>
  );
};

